import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { RoleAccessService } from '../services/role-access.service';
import { Feature } from '../models/feature.model';
import { getFeaturePath } from '../feature-constants';

export const RoleGuard = (requiredFeature: Feature): CanActivateFn => {
  return () => {
    const authService = inject(AuthService);
    const roleAccessService = inject(RoleAccessService);
    const router = inject(Router);

    // Check if user is logged in
    if (!authService.isLoggedIn() || authService.isTokenExpired(authService.getToken() || '')) {
      window.location.href = '/login';
      return false;
    }

    // Check if user has any access at all
    if (!roleAccessService.hasAnyAccess()) {
      console.error('User has no access permissions');
      // Instead of logging out, redirect to a no-access page
      router.navigate(['/no-access']);
      return false;
    }

    // Check if user has access to the required feature using new system
    if (!roleAccessService.canAccess(requiredFeature)) {
      const userRole = roleAccessService.getUserRole();
      const allowedFeatures = roleAccessService.getUserAllowedFeatures();
      console.error(`User with role '${userRole}' does not have access to '${requiredFeature}'`);
      console.log('User allowed features:', allowedFeatures);

      // Redirect to first available feature or no-access page
      if (allowedFeatures.length > 0) {
        // Try to redirect to dashboard first, if not available, go to first available feature
        if (allowedFeatures.includes(Feature.DASHBOARD)) {
          router.navigate(['/dashboard']);
        } else {
          // Redirect to first available feature
          const firstFeature = allowedFeatures[0];
          const featurePath = getFeaturePath(firstFeature);
          if (featurePath) {
            router.navigate([featurePath]);
          } else {
            // Fallback to no-access page if no valid path found
            router.navigate(['/no-access']);
          }
        }
      } else {
        // No features available, redirect to no-access page
        router.navigate(['/no-access']);
      }
      return false;
    }

    return true;
  };
};

// Admin Guard for admin-specific routes
export const AdminGuard = (): CanActivateFn => {
  return () => {
    const authService = inject(AuthService);
    const roleAccessService = inject(RoleAccessService);
    const router = inject(Router);

    // Check if user is logged in
    if (!authService.isLoggedIn() || authService.isTokenExpired(authService.getToken() || '')) {
      window.location.href = '/login';
      return false;
    }

    // Check if user has any access at all
    if (!roleAccessService.hasAnyAccess()) {
      console.error('User has no access permissions');
      // Instead of logging out, redirect to a no-access page
      router.navigate(['/no-access']);
      return false;
    }

    // Check if user has User Management access (this is the only way to access admin routes)
    const hasUserManagement = roleAccessService.canAccess(Feature.USER_MANAGEMENT);

    if (!hasUserManagement) {
      console.error(`User does not have User Management access`);
      const allowedFeatures = roleAccessService.getUserAllowedFeatures();

      // Redirect to first available feature or no-access page
      if (allowedFeatures.length > 0) {
        if (allowedFeatures.includes(Feature.DASHBOARD)) {
          router.navigate(['/dashboard']);
        } else {
          // Redirect to first available feature
          const firstFeature = allowedFeatures[0];
          const featurePath = getFeaturePath(firstFeature);
          if (featurePath) {
            router.navigate([featurePath]);
          } else {
            // Fallback to no-access page if no valid path found
            router.navigate(['/no-access']);
          }
        }
      } else {
        // No features available, redirect to no-access page
        router.navigate(['/no-access']);
      }
      return false;
    }

    return true;
  };
};
